import _get from 'lodash/get';
import _set from 'lodash/set';
import _cloneDeep from 'lodash/cloneDeep';
import _isEmpty from 'lodash/isEmpty';
import _debounce from 'lodash/debounce';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import I18n from 'i18n-js';
import { Form, Icon } from 'grab-ui';
import FeedbackModal from 'components/history/modal/feedback-modal';
import MSDTrackingModal from 'components/history/modal/msd-tracking-modal';
import CancelMultipleConfirmModal from 'components/history/modal/cancel-multiple-confirm-modal';
import InputField from 'components/v2/input-field/input-field';
import { getCompanyID } from 'helper/user';
import { cat, custom, event, trackClick } from 'common/utils/instrumentation';
import OngoingBookingsSection from 'components/v2/ongoing-bookings-section/ongoing-bookings-section';
import PreviousBookingsSection from 'components/v2/previous-bookings-section/previous-bookings-section';
import { FILTER_FIELD } from 'constants/booking-constants';
import {
  COMPLETED_ORDER_STATES,
  GE_WEB_ORDER_STATE,
  MSD_STEP_STATE,
  ORDER_TYPES,
} from 'constants/order-constants';
import {
  scribeTrackEvent,
  getCommonFacet,
  SCRIBE_STATE_NAMES,
  SCRIBE_BOOKING_EVENTS,
  SCRIBE_EVENT_PARAMS,
  SCRIBE_SHOPIFY_EVENTS,
  SCRIBE_SHOPIFY_PARAMS,
} from 'applications/scribe/instrumentations/track';
import ShopifyTrackEventContext from 'views/v2/shopify-home/track-event';
import { isShopifyOrder } from 'helper/order';
import css from './order-history.less';

const getActionDropdownID = order => `ant-dropdown-${order.deliveryID}`;

class OrderHistory extends Component {
  static contextType = ShopifyTrackEventContext;
  static getDerivedStateFromProps(props, state) {
    const { selectedOngoingState } = state;
    const { ongoingOrders, isOngoingOrdersLoading, form } = props;

    return {
      hideOngoingBookingsSection:
        selectedOngoingState === GE_WEB_ORDER_STATE.ActiveDelivery &&
        _isEmpty(form.getFieldValue(FILTER_FIELD.KEYWORDS)) &&
        !isOngoingOrdersLoading &&
        _isEmpty(ongoingOrders),
    };
  }

  constructor(props) {
    super(props);

    this.state = {
      cancelOrderModal: {
        open: false,
        deliveryID: '',
        expressServiceID: undefined,
        isMSDBooking: false,
        shopifyOrderIDs: [],
        isShopifyOrder: false,
      },
      cancelMultipleOrderModel: {
        open: false,
      },
      trackingLinksModal: {
        open: false,
        links: [],
      },
      selectedOngoingState:
        _get(props, 'location.state.ongoingState') ||
        GE_WEB_ORDER_STATE.ActiveDelivery,
      hideOngoingBookingsSection: true,
      selectedOngoingOrderType: ORDER_TYPES.ALL,
      selectedOrders: [],
    };
  }

  // eslint-disable-next-line react/sort-comp
  hideActionDropdown = order => {
    try {
      const actionDropdown = document.getElementById(
        getActionDropdownID(order),
      );
      actionDropdown.click();
    } catch (e) {
      // do nothing
    }
  };

  openMSDTrackingModal = (links = []) => {
    this.setState({
      trackingLinksModal: { open: true, links },
    });
  };

  closeMSDTrackingModal = () => {
    this.setState({
      trackingLinksModal: { open: false },
    });
  };

  onClickTrackOrder = order => () => {
    const { steps, trackingURL, webTrackingURLsMap } = order;
    const ongoingRecipient = steps
      .slice(1)
      .find(
        step => step.status === MSD_STEP_STATE.Ongoing && step.webTrackingURL,
      );

    if (!_isEmpty(ongoingRecipient)) {
      window.open(ongoingRecipient.webTrackingURL);
      this.hideActionDropdown(order);
      return;
    }

    if (!_isEmpty(webTrackingURLsMap)) {
      this.openMSDTrackingModal(webTrackingURLsMap);
      return;
    }

    window.open(trackingURL);
    this.hideActionDropdown(order);
  };

  onClickCancelOrder = order => () => {
    const isMSDBooking = _get(order, 'steps.length') > 2;
    const shopifyMetaDatas = _get(order, 'shopifyMetaData', []);
    const expressServiceID = _get(order, 'service.expressServiceID', 0);
    const shopifyOrderIDs = shopifyMetaDatas.map(metadata => metadata.ID);
    this.props.listCancellationReasons([order]);
    this.setState({
      cancelOrderModal: {
        open: true,
        deliveryID: order.deliveryID,
        expressServiceID,
        isMultiple: false,
        isMSDBooking,
        isShopifyOrder: isShopifyOrder(_get(order, 'thirdPartySource', '')),
        shopifyOrderIDs,
      },
    });
    this.hideActionDropdown(order);
  };

  onSelectOrders = (selectedOrdersKeys, selectedOrders) => {
    this.setState({ selectedOrders });
  };

  onClickCancelMultipleOrders = orders => {
    this.props.listCancellationReasons(orders);
    this.setState({
      cancelMultipleOrderModel: {
        open: true,
      },
    });
    this.setState({
      cancelOrderModal: {
        open: false,
        isMultiple: true,
        orders,
      },
    });
  };

  clearSelectedOrders = () => {
    this.setState({
      selectedOrders: [],
    });
  };

  onHideCancelModal = cancelReq => {
    const { cancelOrderModal } = this.state;
    const {
      cancelOrder,
      submitCancelReason,
      enableWebCancellation,
    } = this.props;
    if (_get(cancelOrderModal, 'isMultiple', false)) {
      this.cancelMultipleOrders(cancelReq);
    } else if (enableWebCancellation) {
      // cancelOrder(cancelOrderModal.deliveryID, cancelOrderModal.isMSDBooking);
      cancelOrder({
        deliveryID: cancelOrderModal.deliveryID,
        isMSDBooking: cancelOrderModal.isMSDBooking,
      });
      submitCancelReason(cancelReq);
    }
    this.setState({ cancelOrderModal: {} });
    this.clearSelectedOrders();
  };

  onHideCancelMultipleModel = () => {
    this.setState({
      cancelMultipleOrderModel: {
        open: false,
      },
    });
  };

  onSubmitCancelMultipleOrder = () => {
    const { selectedOrders } = this.state;
    this.setState({
      cancelMultipleOrderModel: {
        open: false,
      },
    });
    this.setState({
      cancelOrderModal: {
        open: true,
        isMultiple: true,
        orders: selectedOrders,
      },
    });
  };

  cancelMultipleOrders = cancelReq => {
    const { selectedOrders } = this.state;
    const {
      submitCancelReasonsForMultipleOrders,
      enableWebCancellation,
    } = this.props;
    const multipleCancelReq = selectedOrders.map(order => {
      const newReq = {
        ..._cloneDeep(cancelReq),
      };
      const isMSDBooking = _get(order, 'steps.length') > 2;
      const expressServiceID = _get(order, 'service.expressServiceID', 0);
      _set(newReq, 'req.deliveryID', order.deliveryID);
      _set(newReq, 'req.isMSDBooking', isMSDBooking);
      _set(newReq, 'req.expressServiceID', expressServiceID);
      return newReq;
    });

    if (enableWebCancellation) {
      submitCancelReasonsForMultipleOrders(multipleCancelReq);
    }
  };

  onSubmitCancelOrder = cancelReq => {
    const { cancelOrderModal } = this.state;
    const {
      cancelOrder,
      submitCancelReason,
      enableWebCancellation,
    } = this.props;
    if (_get(cancelOrderModal, 'isMultiple', false)) {
      this.cancelMultipleOrders(cancelReq);
    } else {
      cancelOrder({
        deliveryID: cancelOrderModal.deliveryID,
        isMSDBooking: cancelOrderModal.isMSDBooking,
      });
      submitCancelReason(cancelReq);

      this.context.fireShopifyTrackEvent({
        stateName: SCRIBE_STATE_NAMES.EXPRESS_BOOKINGS_WEB,
        eventName: SCRIBE_SHOPIFY_EVENTS.CANCEL_BOOKING,
        facets: {
          [SCRIBE_SHOPIFY_PARAMS.SHOPIFY_ORDER_ID]:
            cancelOrderModal.shopifyOrderIDs,
          [SCRIBE_SHOPIFY_PARAMS.IS_MULTISTOP_BOOKING]:
            cancelOrderModal.isMSDBooking,
          [SCRIBE_SHOPIFY_PARAMS.IS_SHOPIFY_ORDER]:
            cancelOrderModal.isShopifyOrder,
        },
      });
    }

    this.setState({
      cancelOrderModal: {},
    });

    if (enableWebCancellation) {
      this.clearSelectedOrders();
    }
  };

  onSelectOngoingState = value => {
    const { selectedOngoingState } = this.state;
    const { userInfo } = this.props;
    const companyID = getCompanyID(userInfo);
    trackClick(
      event.BOOKINGS_CLICKS,
      cat.GE_BOOKINGS,
      'ONGOING_BOOKINGS_STATUS',
      {
        [custom.COMPANY_ID]: companyID,
      },
    );
    scribeTrackEvent({
      stateName: SCRIBE_STATE_NAMES.EXPRESS_BOOKINGS_WEB,
      eventName: SCRIBE_BOOKING_EVENTS.ONGOING_BOOKINGS_STATUS,
      facets: getCommonFacet(userInfo),
    });

    if (selectedOngoingState !== value) {
      this.setState({ selectedOngoingState: value });
    }
  };

  onSelectOngoingOrderType = value => {
    const { selectedOngoingOrderType } = this.state;
    const { userInfo } = this.props;

    if (value === ORDER_TYPES.SHOPIFY) {
      this.context.fireShopifyTrackEvent({
        stateName: SCRIBE_STATE_NAMES.EXPRESS_BOOKINGS_WEB,
        eventName: SCRIBE_SHOPIFY_EVENTS.FILTER_SHOPIFY_ORDERS,
      });
    }

    scribeTrackEvent({
      stateName: SCRIBE_STATE_NAMES.EXPRESS_BOOKINGS_WEB,
      eventName: SCRIBE_BOOKING_EVENTS.ONGOING_BOOKINGS_FILTER,
      facets: {
        ...getCommonFacet(userInfo),
        [SCRIBE_EVENT_PARAMS.ONGOING_FILTER_NAME]: I18n.t(
          `history.orderTypes.${value.toLowerCase()}`,
          { locale: 'en' },
        ),
      },
    });

    if (selectedOngoingOrderType !== value) {
      this.setState({ selectedOngoingOrderType: value });
    }
  };

  renderOngoingBookingsSection = () => {
    const {
      selectedOngoingState,
      hideOngoingBookingsSection,
      selectedOngoingOrderType,
    } = this.state;

    if (hideOngoingBookingsSection) {
      return null;
    }

    const {
      ongoingOrders,
      ongoingOrdersLastUpdate,
      isOngoingOrdersLoading,
      userInfo,
      enableWebCancellation,
      onClickPrintLabel,
      onClickBulkPrintLabel,
      scribeExpressBookingsWebShowOngoingBookings,
    } = this.props;

    const { selectedOrders } = this.state;

    return (
      <OngoingBookingsSection
        ongoingOrders={ongoingOrders}
        selectedOrders={selectedOrders}
        ongoingOrdersLastUpdate={ongoingOrdersLastUpdate}
        isOngoingOrdersLoading={isOngoingOrdersLoading}
        userInfo={userInfo}
        onClickTrackOrder={this.onClickTrackOrder}
        onClickCancelOrder={this.onClickCancelOrder}
        onClickCancelMultipleOrders={this.onClickCancelMultipleOrders}
        onSelectOngoingState={this.onSelectOngoingState}
        selectedOngoingState={selectedOngoingState}
        onSelectOngoingOrderType={this.onSelectOngoingOrderType}
        selectedOngoingOrderType={selectedOngoingOrderType}
        onSelectOrders={this.onSelectOrders}
        onClickPrintLabel={onClickPrintLabel}
        onClickBulkPrintLabel={onClickBulkPrintLabel}
        scribeExpressBookingsWebShowOngoingBookings={
          scribeExpressBookingsWebShowOngoingBookings
        }
        enableWebCancellation={enableWebCancellation}
      />
    );
  };

  renderPreviousBookingsSection = () => {
    const {
      userInfo,
      ongoingOrders,
      ongoingOrdersLastUpdate,
      completedOrders,
      completedOrdersSourceOptions,
      updateOrderShallow,
      bookAgain,
      shopifyBookAgain,
      form,
      isPreviousBookingsLoading,
    } = this.props;

    return (
      <PreviousBookingsSection
        userInfo={userInfo}
        ongoingOrders={ongoingOrders}
        ongoingOrdersLastUpdate={ongoingOrdersLastUpdate}
        completedOrders={completedOrders}
        completedOrdersSourceOptions={completedOrdersSourceOptions}
        updateOrderShallow={updateOrderShallow}
        bookAgain={bookAgain}
        shopifyBookAgain={shopifyBookAgain}
        isPreviousBookingsLoading={isPreviousBookingsLoading}
        form={form}
      />
    );
  };

  render() {
    const {
      trackingLinksModal,
      cancelOrderModal,
      selectedOrders,
      cancelMultipleOrderModel,
    } = this.state;
    const { cancelReasons, form, isOngoingOrdersLoading } = this.props;
    return (
      <div>
        <h4 className={css.pageHeader}>Bookings</h4>
        <InputField
          id={FILTER_FIELD.KEYWORDS}
          placeholder={I18n.t('v2.bookingsPage.searchInput')}
          suffix={<Icon type="search" />}
          className={css.searchInput}
          form={form}
          disabled={isOngoingOrdersLoading}
        />
        {this.renderOngoingBookingsSection()}
        {this.renderPreviousBookingsSection()}
        <CancelMultipleConfirmModal
          orderAmt={selectedOrders.length}
          handleCancelOrderModal={this.onSubmitCancelMultipleOrder}
          handleClose={this.onHideCancelMultipleModel}
          visible={cancelMultipleOrderModel.open}
        />
        <FeedbackModal
          visible={cancelOrderModal.open}
          closeable
          deliveryID={cancelOrderModal.deliveryID}
          expressServiceID={cancelOrderModal.expressServiceID}
          isMSDBooking={cancelOrderModal.isMSDBooking}
          cancelReasons={cancelReasons}
          handleSubmit={this.onSubmitCancelOrder}
          onCancel={this.onHideCancelModal}
        />
        <MSDTrackingModal
          visible={trackingLinksModal.open}
          trackingURLsMap={trackingLinksModal.links}
          handleClose={this.closeMSDTrackingModal}
        />
      </div>
    );
  }
}

OrderHistory.propTypes = {
  userInfo: PropTypes.shape({}).isRequired,
  ongoingOrders: PropTypes.arrayOf(PropTypes.shape({})),
  isOngoingOrdersLoading: PropTypes.bool.isRequired,
  ongoingOrdersLastUpdate: PropTypes.string,
  completedOrders: PropTypes.arrayOf(PropTypes.shape({})),
  completedOrdersSourceOptions: PropTypes.arrayOf(PropTypes.number),
  cancelReasons: PropTypes.arrayOf(PropTypes.shape({})),
  cancelOrder: PropTypes.func.isRequired,
  updateOrderShallow: PropTypes.func.isRequired,
  bookAgain: PropTypes.func.isRequired,
  shopifyBookAgain: PropTypes.func.isRequired,
  form: PropTypes.shape({}).isRequired,
  isPreviousBookingsLoading: PropTypes.bool.isRequired,
  submitCancelReason: PropTypes.func.isRequired,
  submitCancelReasonsForMultipleOrders: PropTypes.func.isRequired,
  listCancellationReasons: PropTypes.func.isRequired,
  onClickPrintLabel: PropTypes.func.isRequired,
  onClickBulkPrintLabel: PropTypes.func.isRequired,
  scribeExpressBookingsWebShowOngoingBookings: PropTypes.func.isRequired,
  enableWebCancellation: PropTypes.bool.isRequired,
};

OrderHistory.defaultProps = {
  ongoingOrders: [],
  ongoingOrdersLastUpdate: '',
  completedOrders: [],
  completedOrdersSourceOptions: [],
  cancelReasons: [],
};

const onFieldsChange = _debounce((props, changedValues, allValues) => {
  const allState = COMPLETED_ORDER_STATES.find(
    s => s.state === GE_WEB_ORDER_STATE.AllStatus,
  ).stateToQuery;

  const keywords = allValues[FILTER_FIELD.KEYWORDS].value;
  const isKeywordsChanged = !_isEmpty(changedValues[FILTER_FIELD.KEYWORDS]);

  const {
    userInfo,
    fetchOngoingDeliveriesInterval,
    fetchAllPreviousBookings,
    stopFetchOngoingDeliveriesInterval,
  } = props;
  const companyID = getCompanyID(userInfo);

  if (isKeywordsChanged) {
    stopFetchOngoingDeliveriesInterval();
    fetchOngoingDeliveriesInterval({ companyID, keywords });

    scribeTrackEvent({
      stateName: SCRIBE_STATE_NAMES.EXPRESS_BOOKINGS_WEB,
      eventName: SCRIBE_BOOKING_EVENTS.SEARCH_INPUT,
      facets: getCommonFacet(userInfo),
    });

    scribeTrackEvent({
      stateName: SCRIBE_STATE_NAMES.EXPRESS_BOOKINGS_WEB,
      eventName: SCRIBE_BOOKING_EVENTS.SEARCH,
      facets: {
        ...getCommonFacet(userInfo),
        [SCRIBE_EVENT_PARAMS.SEARCH_TEXT]: keywords,
      },
    });
  }

  const fromDate = allValues.DATE_RANGE.value[0].format('YYYY-MM-DD');
  const toDate = allValues.DATE_RANGE.value[1].format('YYYY-MM-DD');
  const fetchPrevBookingParams = {
    companyID,
    fromDate, // mandatory
    toDate, // mandatory
    states: allState, // at the moment since order type has no effect, simplify fetch all and filter on frontend
    limit: -1, // -1 Means Fetch All
    orderType: ORDER_TYPES.ALL, // at the moment order type params has no filter effect from backend
  };
  if (!_isEmpty(keywords)) {
    fetchPrevBookingParams.keywords = keywords;
  }
  fetchAllPreviousBookings(fetchPrevBookingParams);
}, 700);

export default Form.create({ onFieldsChange })(OrderHistory);
